import { useState } from "react";
import { Check, Info, Minus } from "lucide-react";
import { featureInfo } from "@/lib/featureInfo";
import { planCost, planId } from "@/lib/plans";
import { planGroups } from "@/components/login/PlanDetails";
import { cn } from "@/lib/utils";

const INHERIT = /^everything in (\w+)/i;

const buildMatrix = (plans) => {
  const own = Object.fromEntries(plans.map((p) => [planId(p), planGroups(p)]));
  const free = plans.find((p) => planId(p) === "free");
  const sets = {};
  const resolve = (id, seen = new Set()) => {
    if (sets[id]) return sets[id];
    if (seen.has(id)) return new Set();
    seen.add(id);
    const s = new Set();
    if (id !== "free" && free) own.free.forEach((g) => g.items.forEach((t) => s.add(t)));
    (own[id] || []).forEach((g) => g.items.forEach((t) => { if (!INHERIT.test(t)) s.add(t); }));
    const p = plans.find((x) => planId(x) === id) || {};
    const hints = [...(p.features || []), ...(p.groups || []).flatMap((g) => [g.title, ...(g.items || [])])];
    hints.forEach((t) => {
      const m = String(t || "").match(INHERIT);
      const parent = m && plans.find((x) => (x.name || planId(x)).toLowerCase() === m[1].toLowerCase());
      if (parent && planId(parent) !== id) resolve(planId(parent), seen).forEach((x) => s.add(x));
    });
    return (sets[id] = s);
  };
  plans.forEach((p) => resolve(planId(p)));
  const groups = [];
  const seenRow = new Set();
  plans.forEach((p) => own[planId(p)].forEach((g) => {
    const items = g.items.filter((t) => !INHERIT.test(t) && !seenRow.has(t));
    items.forEach((t) => seenRow.add(t));
    if (!items.length) return;
    const title = planId(p) === "free" ? "Included free" : g.title;
    const existing = groups.find((x) => x.title === title);
    existing ? existing.items.push(...items) : groups.push({ title, items: [...items] });
  }));
  return { groups, sets };
};

export const PlanCompare = ({ plans, active, yearly, onPick }) => {
  const { groups, sets } = buildMatrix(plans);
  const [openRow, setOpenRow] = useState(null);
  const cols = "grid-cols-[minmax(0,1fr)_repeat(var(--n),3.25rem)] sm:grid-cols-[minmax(0,1fr)_repeat(var(--n),5.5rem)]";
  return (
    <div className="px-3 pb-4 sm:px-6" style={{ "--n": plans.length }} data-testid="plan-compare">
      <div className={cn("sticky top-0 z-10 grid items-end gap-1 border-b border-[#27272A] bg-[#121214]/95 py-3 backdrop-blur-md", cols)}>
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Feature <span className="hidden normal-case tracking-normal text-zinc-600 sm:inline">· tap one to learn more</span></span>
        {plans.map((p) => {
          const id = planId(p), on = id === active, c = planCost(p, yearly);
          return (
            <button key={id} onClick={() => onPick(id)} data-testid={`plan-compare-col-${id}`}
              className={cn("flex flex-col items-center rounded-lg px-1 py-1.5 transition-colors", on ? "bg-gradient-to-b from-[#D4AF37] to-[#b8952c] text-black shadow-[0_0_20px_rgba(212,175,55,0.35)]" : "text-zinc-300 hover:bg-[#18181B]")}>
              <span className="text-[11px] font-bold uppercase tracking-wider">{p.name || id}</span>
              <span className={cn("font-mono text-[10px]", on ? "text-black/70" : "text-zinc-500")}>{c.price}</span>
            </button>
          );
        })}
      </div>
      {groups.map((g) => (
        <div key={g.title} className="mt-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">{g.title}</div>
          {g.items.map((t, i) => {
            const info = featureInfo(t, plans);
            const open = openRow === t;
            return (
            <div key={t} className={cn("rounded-md", i % 2 ? "" : "bg-[#18181B]/50", open && "bg-[#33290D]/40 ring-1 ring-[#D4AF37]/30")} data-testid="plan-compare-row">
            <div className={cn("grid items-center gap-1 py-1.5 text-[12px] sm:text-sm", cols)}>
              {info ? (
                <button onClick={() => setOpenRow(open ? null : t)} aria-expanded={open} data-testid="plan-compare-feature"
                  className="group flex items-start gap-1.5 px-2 text-left leading-snug text-zinc-300 transition-colors hover:text-zinc-50">
                  <span className="underline decoration-[#D4AF37]/40 decoration-dotted underline-offset-4 group-hover:decoration-[#D4AF37]">{t}</span>
                  <Info className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 transition-colors", open ? "text-[#F3C649]" : "text-zinc-600 group-hover:text-[#D4AF37]")} />
                </button>
              ) : <span className="px-2 leading-snug text-zinc-300">{t}</span>}
              {plans.map((p) => {
                const id = planId(p), has = sets[id].has(t), on = id === active;
                return (
                  <span key={id} className={cn("flex h-full items-center justify-center", on && "bg-[#D4AF37]/10")} data-testid={`plan-compare-cell-${id}`} aria-label={has ? "Included" : "Not included"}>
                    {has ? <span className={cn("flex h-5 w-5 items-center justify-center rounded-full", on ? "bg-[#D4AF37] text-black" : "bg-[#33290D] text-[#F3C649]")}><Check className="h-3 w-3" strokeWidth={3} /></span> : <Minus className="h-3.5 w-3.5 text-zinc-700" />}
                  </span>
                );
              })}
            </div>
            {open && <p className="fade-up px-3 pb-2.5 text-xs leading-relaxed text-[#F3C649]/90" data-testid="plan-compare-info">{info}</p>}
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
